/**
 * useActionTimeout - Simple timeout detection for long-running actions
 *
 * Only handles timeout detection - the parent component handles all other loading states.
 */

import { useState, useCallback, useEffect, useRef } from 'react'

export interface UseActionTimeoutOptions {
  timeoutMs?: number
  isLoading: boolean
  isSuccess: boolean
  error?: Error | null
  onTimeout?: () => void
}

export interface UseActionTimeoutResult {
  isClicked: boolean
  isTimedOut: boolean
  displayError: Error | null
  handleActionClick: (action: () => void) => void
  retry: () => void
  reset: () => void
  isActionLoading: boolean
}

export class ActionTimeoutError extends Error {
  constructor(message = 'The operation is taking longer than expected. Please try again.') {
    super(message)
    this.name = 'ActionTimeoutError'
  }
}

export function useActionTimeout({
  timeoutMs = 15000,
  isLoading,
  isSuccess,
  error,
  onTimeout,
}: UseActionTimeoutOptions): UseActionTimeoutResult {
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Clear timeout when operation completes (success or error)
  useEffect(() => {
    if (isSuccess || error) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setIsTimedOut(false)
    }
  }, [isSuccess, error])

  const handleActionClick = useCallback(
    (action: () => void) => {
      setIsTimedOut(false)
      setPendingAction(() => action)

      // Start timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true)
        onTimeout?.()
      }, timeoutMs)

      action()
    },
    [timeoutMs, onTimeout],
  )

  const retry = useCallback(() => {
    setIsTimedOut(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setIsTimedOut(true)
      onTimeout?.()
    }, timeoutMs)

    if (pendingAction) {
      pendingAction()
    }
  }, [pendingAction, timeoutMs, onTimeout])

  const reset = useCallback(() => {
    setIsTimedOut(false)
    setPendingAction(null)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const displayError = isTimedOut ? new ActionTimeoutError() : null

  // isClicked is no longer tracked - let parent handle loading states
  // isActionLoading just reflects wagmi's loading state
  const isClicked = false
  const isActionLoading = isLoading

  return {
    isClicked,
    isTimedOut,
    displayError,
    handleActionClick,
    retry,
    reset,
    isActionLoading,
  }
}

export function isTimeoutError(error: Error | null | undefined): boolean {
  return error instanceof ActionTimeoutError || error?.name === 'ActionTimeoutError'
}
